










import { Component, Prop, Watch, Vue } from 'vue-property-decorator';
import Konva from 'konva';
import { gsap } from 'gsap';
import events from '../data/events';
import {
  sortEventsByDate,
  getAllEventsUpToDate,
  getEventsByParticipant,
  getEventIndex,
} from '../utils/eventsUtils';

interface Config {
  x: number;
  y: number;
  rotation: number;
  opacity: number;
  date: Date;
}

@Component
export default class Footprints extends Vue {
  @Prop(String) readonly footprintsOf!: string;
  @Prop(Number) readonly index!: number;
  @Prop(Boolean) readonly hobbits!: boolean;

  get currentEvent() {
    return this.$store.getters['events/getCurrentEvent'];
  }

  events = sortEventsByDate(getEventsByParticipant(events, this.footprintsOf));

  configImage = {
    image: new Image(),
    scale: {
      x: 0.3 - (this.hobbits ? 0.1 : 0),
      y: 0.3 - (this.hobbits ? 0.1 : 0),
    },
    offsetX: 0,
    offsetY: 0,
  };

  configs: Config[] = [];

  addImg(name: string): void {
    this.configImage.image.src = require(`../assets/footprints - ${name}.svg`);
  }

  getAngle(
    point1: { x: number; y: number },
    point2: { x: number; y: number },
  ): number {
    //get an angle of a vector placed at point1 and ending at point2

    if (point1.x === point2.x) {
      return point2.y > point1.y ? 90 : 270;
    }
    let angleRad = Math.atan((point2.y - point1.y) / (point2.x - point1.x));
    if (point2.x < point1.x) {
      angleRad += Math.PI;
    }
    if (angleRad < 0) {
      angleRad += 2 * Math.PI;
    }
    const angleDeg = (angleRad * 180) / Math.PI;
    return angleDeg;
  }

  mounted() {
    this.addImg(this.footprintsOf);
  }

  @Watch('currentEvent')
  onEventChange(newEvent: { date: Date }, oldEvent: { date: Date }) {
    if (!oldEvent) return;

    const animateFootprints = () => {
      if (newEvent.date.valueOf() > oldEvent.date.valueOf()) {
        const eventsToDate = getAllEventsUpToDate(this.events, newEvent.date);

        const newConfigs: Config[] = [];

        eventsToDate.map(evnt => {
          if (evnt.paths) {
            const path = new Konva.Path({
              data: evnt.paths[this.footprintsOf],
            });
            const nextEvent = this.events[getEventIndex(this.events, evnt) + 1];

            const start =
              (oldEvent.date.valueOf() - evnt.date.valueOf()) /
              (nextEvent.date.valueOf() - evnt.date.valueOf());
            const end =
              (newEvent.date.valueOf() - evnt.date.valueOf()) /
              (nextEvent.date.valueOf() - evnt.date.valueOf());

            for (
              let i = (start > 0 ? start : 0) * path.getLength() + 10;
              i < (end < 1 ? end : 1) * path.getLength() - 10;
              i += 40
            ) {
              const startPoint = path.getPointAtLength(i);
              const endPoint = path.getPointAtLength(i + 10);

              const config = {
                x: startPoint.x + this.index * 2,
                y: startPoint.y + this.index * 2,
                rotation:
                  this.getAngle(
                    { x: startPoint.x, y: startPoint.y },
                    { x: endPoint.x, y: endPoint.y },
                  ) + 90,
                opacity: 0,
                date: newEvent.date,
              };

              newConfigs.push(config);
            }
          }
        });

        const tl = gsap.timeline({
          autoRemoveChildren: true,
          paused: true,
          defaults: { duration: 0.5 },
        });

        newConfigs.map(config => {
          const opacity = { val: 0 };
          tl.to(
            opacity,
            {
              val: 1,
              onUpdate: () => (config.opacity = opacity.val),
            },
            '<0.2',
          );
        });

        tl.duration(2);
        this.configs = [...this.configs, ...newConfigs];
        tl.resume();
      } else if (newEvent.date.valueOf() < oldEvent.date.valueOf()) {
        const eventsToDate = getAllEventsUpToDate(this.events, oldEvent.date);

        const newConfigs: Config[] = [];
        const oldConfigs: Config[] = [];

        eventsToDate.map(evnt => {
          if (evnt.paths && evnt.date.valueOf() <= newEvent.date.valueOf()) {
            const path = new Konva.Path({
              data: evnt.paths[this.footprintsOf],
            });
            const nextEvent = this.events[getEventIndex(this.events, evnt) + 1];

            let end =
              (newEvent.date.valueOf() - evnt.date.valueOf()) /
              (nextEvent.date.valueOf() - evnt.date.valueOf());

            for (
              let i = 10;
              i < (end < 1 ? end : 1) * path.getLength() - 10;
              i += 40
            ) {
              const startPoint = path.getPointAtLength(i);
              const endPoint = path.getPointAtLength(i + 10);

              const config = {
                x: startPoint.x + this.index * 2,
                y: startPoint.y + this.index * 2,
                rotation:
                  this.getAngle(
                    { x: startPoint.x, y: startPoint.y },
                    { x: endPoint.x, y: endPoint.y },
                  ) + 90,
                opacity: 1,
                date: newEvent.date,
              };

              newConfigs.push(config);
            }

            const start =
              (newEvent.date.valueOf() - evnt.date.valueOf()) /
              (nextEvent.date.valueOf() - evnt.date.valueOf());
            end =
              (oldEvent.date.valueOf() - evnt.date.valueOf()) /
              (nextEvent.date.valueOf() - evnt.date.valueOf());

            for (
              let i = (start > 0 ? start : 0) * path.getLength() + 10;
              i < (end < 1 ? end : 1) * path.getLength() - 10;
              i += 40
            ) {
              const startPoint = path.getPointAtLength(i);
              const endPoint = path.getPointAtLength(i + 10);

              const config = {
                x: startPoint.x + this.index * 2,
                y: startPoint.y + this.index * 2,
                rotation:
                  this.getAngle(
                    { x: startPoint.x, y: startPoint.y },
                    { x: endPoint.x, y: endPoint.y },
                  ) + 90,
                opacity: 1,
                date: newEvent.date,
              };

              oldConfigs.push(config);
            }
          }
        });

        const tl = gsap.timeline({
          autoRemoveChildren: true,
          paused: true,
          onComplete: () => (this.configs = [...newConfigs]),
          defaults: { duration: 0.5 },
        });

        oldConfigs.map(config => {
          const opacity = { val: 1 };
          tl.to(
            opacity,
            {
              val: 0,
              onUpdate: () => (config.opacity = opacity.val),
            },
            '<',
          );
        });

        tl.duration(1);
        this.configs = [...newConfigs, ...oldConfigs];
        tl.resume();
      }
    };

    setTimeout(animateFootprints, 100 * this.index);
  }
}
